import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { WordmarkTagline } from "@/components/Logo";
import { api } from "@/lib/api";
import { formatGBP } from "@/lib/utils-cart";
import { ArrowRight, Package, Store, Sparkles, Wallet, Truck, BarChart3, CheckCircle2 } from "lucide-react";

// Example parcels for the departures-board strip under the hero. They are
// labelled "Example dispatches" on the page: illustrations, not live orders.
const DISPATCHES = [
  ["Kendal", "Bristol", "Fellrunner Trail Shoes", 110],
  ["Holt", "Edinburgh", "Wild Fig Candle 250g", 28],
  ["Leeds", "Cardiff", "Aurora Wireless Headphones", 199],
  ["Northampton", "Belfast", "Northampton Court Trainers", 135],
  ["Bristol", "Manchester", "Bristol Roast Espresso Blend", 9.5],
  ["Kendal", "Inverness", "Borrowdale Waterproof Jacket", 249],
  ["London", "York", "Oak Laptop Stand", 69],
  ["Holt", "Brighton", "Terracotta Self-Watering Planter", 38],
];

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/products?limit=8").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get("/stores").then((r) => setStores(r.data.filter((s) => !s.system_store).slice(0, 3))).catch(() => {});
    api.get("/system/health").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border min-h-[calc(100svh-4rem)] flex flex-col">
        <div className="absolute inset-0 grid-bg opacity-40 hero-grid-drift" />
        <div className="absolute top-20 right-0 w-1/3 h-2/3 bg-gradient-to-bl from-secondary/10 via-transparent to-transparent" />
        <div className="relative flex-1 w-full max-w-7xl mx-auto px-6 pt-16 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-secondary mb-6 hero-rise" style={{ "--i": 0 }} data-testid="hero-eyebrow">
              The Commerce Operating System · United Kingdom
            </div>
            <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-primary">
              <span className="block hero-rise" style={{ "--i": 1 }}>Build a proper</span>
              <span className="block hero-rise" style={{ "--i": 2 }}>British shop,</span>
              <span className="block text-secondary hero-rise" style={{ "--i": 3 }}>not just another storefront.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed hero-rise" style={{ "--i": 4 }}>
              Vendora brings sourcing, selling, delivery and growth into one place. Stock up from vetted UK suppliers, open a shop under your own name, recruit affiliates, and grow with built-in CRM, AI marketing and analytics.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 hero-rise" style={{ "--i": 5 }}>
              <Link to="/register" className="v-btn-primary text-base px-7 py-3" data-testid="hero-cta-start">
                Start free <ArrowRight size={16} />
              </Link>
              <Link to="/marketplace" className="v-btn-outline text-base px-7 py-3" data-testid="hero-cta-explore">
                Explore the marketplace
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground hero-rise" style={{ "--i": 6 }}>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent-green" /> No setup fee</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent-green" /> Centralised payments</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent-green" /> Priced in pounds, VAT included</span>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <div className="v-card aspect-square flex flex-col justify-between hero-rise" style={{ "--i": 4 }}>
              <Package className="text-secondary" size={28} />
              <div>
                <div className="font-display font-black text-3xl text-primary">{stats?.products ?? "18+"}</div>
                <div className="text-xs text-muted-foreground mt-1">products from UK suppliers</div>
              </div>
            </div>
            <div className="v-card aspect-square flex flex-col justify-between bg-primary text-primary-foreground border-primary hero-rise" style={{ "--i": 5 }}>
              <Wallet size={28} />
              <div>
                <div className="font-display font-black text-3xl">5%</div>
                <div className="text-xs text-white/70 mt-1">flat platform fee</div>
              </div>
            </div>
            <div className="v-card aspect-square flex flex-col justify-between bg-secondary text-secondary-foreground border-secondary hero-rise" style={{ "--i": 6 }}>
              <Sparkles size={28} />
              <div>
                <div className="font-display font-black text-3xl">AI</div>
                <div className="text-xs text-white/80 mt-1">copy in British English</div>
              </div>
            </div>
            <div className="v-card aspect-square flex flex-col justify-between hero-rise" style={{ "--i": 7 }}>
              <Truck className="text-accent-blue" size={28} />
              <div>
                <div className="font-display font-black text-3xl text-primary">£50</div>
                <div className="text-xs text-muted-foreground mt-1">and over ships free in the UK</div>
              </div>
            </div>
          </div>
        </div>

        {/* Departures board: example parcels leaving UK sellers */}
        <div className="relative border-t border-border bg-primary text-primary-foreground">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-6">
            <div className="hidden md:block flex-shrink-0"><WordmarkTagline inverted /></div>
            <div className="label-eyebrow text-white/50 flex-shrink-0">Example dispatches</div>
            <div className="dispatch-window flex-1 overflow-hidden" aria-hidden="true">
              <div className="dispatch-track flex w-max font-mono text-xs tracking-wider">
                {[...DISPATCHES, ...DISPATCHES].map(([from, to, item, price], i) => (
                  <span key={i} className="flex items-center gap-2 whitespace-nowrap pr-10">
                    <span className="text-[#FFB547]">{from.toUpperCase()}</span>
                    <span className="text-white/40">to</span>
                    <span className="text-[#FFB547]">{to.toUpperCase()}</span>
                    <span className="text-white/70">{item}</span>
                    <span className="text-white">{formatGBP(price)}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Four Verbs */}
      <section className="v-section-pad border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-24">
              <div className="label-eyebrow text-secondary mb-4">Four verbs. One platform.</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">
                Everything you need to run<br />an online shop in the UK.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Stop stitching together five tools. Vendora is one stack, from the wholesale catalogue to the customer's inbox.
              </p>
            </div>

            <div className="lg:col-span-8 grid md:grid-cols-2 gap-3">
              {[
                { icon: Package, color: "text-primary", verb: "SOURCE", title: "A real supplier network", body: "Browse vetted UK suppliers and add their stock to your shop in a few clicks." },
                { icon: Store, color: "text-secondary", verb: "SELL", title: "A shop under your own name", body: "Open a branded shop at vendra.uk/store/your-name with your own colours, banners and pages." },
                { icon: Truck, color: "text-accent-blue", verb: "SHIP", title: "Centralised fulfilment", body: "One inbox for orders, tracking and returns. Use our fulfilment partners or your own Royal Mail, DPD or Evri account." },
                { icon: BarChart3, color: "text-accent-green", verb: "GROW", title: "Marketing, CRM, analytics", body: "AI-written product copy in British English, email and SMS campaigns, customer segments and joined-up analytics." },
              ].map((f) => (
                <div key={f.verb} className="v-card flex flex-col">
                  <div className={`label-eyebrow ${f.color}`}>{f.verb}</div>
                  <f.icon size={32} strokeWidth={1.5} className={`${f.color} mt-6 mb-8`} />
                  <h3 className="font-display text-xl font-semibold text-primary">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace teaser */}
      <section className="v-section-pad bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <div className="label-eyebrow text-secondary mb-3">SOURCE.</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">From the Vendora Marketplace</h2>
            </div>
            <Link to="/marketplace" className="v-btn-outline" data-testid="marketplace-see-all">See all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {products.slice(0, 8).map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group v-card p-3 flex flex-col" data-testid={`marketplace-product-${p.id}`}>
                <div className="aspect-square overflow-hidden bg-muted">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="pt-3 flex-1 flex flex-col">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{p.category}</div>
                  <h3 className="text-sm font-semibold text-primary mt-1 leading-snug line-clamp-2">{p.title}</h3>
                  <div className="mt-auto pt-2 font-display font-bold text-lg text-foreground">{formatGBP(p.price)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Store Builder showcase */}
      <section className="v-section-pad border-b border-border">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow text-secondary mb-3">SELL.</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">Your shop. Your brand. Open in minutes.</h2>
            <p className="mt-6 text-muted-foreground max-w-md">
              Pick a web address, add your logo, choose your colours. Vendora hosts the lot: catalogue, checkout, payments, fulfilment and even your customer service inbox.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["Your own address at vendra.uk/store/your-shop", "Branded checkout with Square and PayPal", "Prices shown in pounds, VAT included", "Built-in SEO, reviews and analytics"].map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-green mt-0.5" /> {x}</li>
              ))}
            </ul>
            <Link to="/register" className="v-btn-primary mt-10" data-testid="storebuilder-cta">Open a shop <ArrowRight size={16} /></Link>
          </div>
          <Link to="/store/saltmarsh-studio" className="relative block group" data-testid="storebuilder-example">
            <div className="absolute -top-4 -left-4 right-8 bottom-8 bg-secondary/10 rounded-sm" />
            <div className="relative v-card p-0 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1753351052617-62818ffc9173?w=900&q=80" alt="An independent shop owner at work" className="w-full h-[420px] object-cover" />
              <div className="p-6">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-accent-green" />
                  <span className="font-mono text-xs tracking-wider">vendra.uk/store/saltmarsh-studio</span>
                </div>
                <h3 className="font-display text-xl font-bold text-primary mt-2">Saltmarsh Studio</h3>
                <p className="text-sm text-muted-foreground mt-1">Coastal homeware, candles and gifts from the north Norfolk coast. A demo shop.</p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Affiliate network */}
      <section className="v-section-pad bg-primary text-primary-foreground border-b border-primary/40 relative noise-overlay overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-secondary mb-4">GROW.</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none">The affiliate and creator network you don't have to build.</h2>
            <p className="mt-6 text-white/70 max-w-xl">
              Every Vendora seller can work with affiliates and creators who promote their products. Generate referral links, track conversions and pay commission automatically.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {[["10%", "default commission"], ["7d", "approval window"], ["∞", "links per affiliate"]].map(([n, l]) => (
                <div key={n} className="border border-white/15 p-4 rounded-sm">
                  <div className="font-display font-black text-3xl">{n}</div>
                  <div className="text-[11px] text-white/60 mt-1 uppercase tracking-wider">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/15 rounded-sm p-6 bg-white/5">
              <div className="label-eyebrow text-secondary mb-4">Affiliate dashboard, example</div>
              <div className="space-y-3 font-mono text-xs">
                <Row left="VND-2A8C" right="£110.00 · paid · square" />
                <Row left="VND-D7F1" right="£199.00 · paid · paypal" />
                <Row left="VND-92BB" right="£42.00 · pending · square" />
                <Row left="VND-1FA4" right="£159.00 · paid · paypal" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business services */}
      <section className="v-section-pad bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-eyebrow text-secondary mb-3">Beyond commerce</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">A marketplace for the rest of your business.</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl">Accounting, payroll, legal, insurance, IT and cyber security from vetted UK providers, with transparent pricing, paid through Vendora.</p>
            </div>
            <Link to="/services" className="v-btn-outline">Browse services <ArrowRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((s) => (
              <div key={s.id} className="v-card flex flex-col">
                <div className="label-eyebrow text-secondary">{s.category.replace("_", " ")}</div>
                <h3 className="mt-4 font-display text-lg font-semibold text-primary">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-1">{s.description}</p>
                <div className="mt-4 pt-4 border-t border-border text-sm">
                  <span className="font-mono">from {formatGBP(s.price_from)} / {s.pricing_unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section id="pricing" className="v-section-pad border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label-eyebrow text-secondary mb-3">Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">No subscriptions. One flat fee.</h2>
            <p className="mt-4 text-muted-foreground">5% per sale. That's it. We only make money when you do.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-3 max-w-5xl mx-auto">
            {[
              { name: "Starter", price: "Free", subtitle: "Up to 50 orders a month", features: ["1 shop", "Square and PayPal checkout", "AI marketing (50 credits)", "Email support"] },
              { name: "Scale", price: "5% per sale", featured: true, subtitle: "Unlimited orders", features: ["Unlimited shops", "Unlimited AI marketing", "Affiliate network access", "Priority support"] },
              { name: "Enterprise", price: "Custom", subtitle: "Volume-based pricing", features: ["Multi-warehouse", "Hands-on onboarding", "API access", "Dedicated account manager"] },
            ].map((p) => (
              <div key={p.name} className={`v-card flex flex-col ${p.featured ? "border-secondary border-2" : ""}`}>
                {p.featured && <div className="label-eyebrow text-secondary mb-2">Most popular</div>}
                <h3 className="font-display text-xl font-bold text-primary">{p.name}</h3>
                <div className="mt-2 font-display font-black text-4xl text-foreground">{p.price}</div>
                <p className="mt-1 text-xs text-muted-foreground">{p.subtitle}</p>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {p.features.map((f) => <li key={f} className="flex gap-2"><CheckCircle2 size={16} className="text-accent-green flex-shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
                <Link to="/register" className={`mt-6 ${p.featured ? "v-btn-primary" : "v-btn-outline"}`}>Get started</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo shops: real store pages, seeded as demos */}
      {stores.length > 0 && (
        <section className="v-section-pad bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-eyebrow text-secondary mb-3 text-center">Have a look round</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary text-center leading-none">Walk through a demo shop.</h2>
            <p className="mt-4 text-muted-foreground text-center max-w-xl mx-auto">These shops are demos, set up to show what a Vendora shop looks like from the customer's side.</p>
            <div className="mt-12 grid md:grid-cols-3 gap-3">
              {stores.map((s) => (
                <Link key={s.id} to={`/store/${s.slug}`} className="v-card flex flex-col group" data-testid={`landing-demo-store-${s.slug}`}>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">vendra.uk/store/{s.slug}</div>
                  <h3 className="mt-2 font-display text-xl font-bold text-primary">{s.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground flex-1">{s.tagline}</p>
                  <div className="mt-6 pt-4 border-t border-border text-sm text-secondary font-medium flex items-center gap-1">Visit the shop <ArrowRight size={14} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="v-section-pad">
        <div className="max-w-5xl mx-auto px-6 v-card border-primary border-2 text-center p-12 lg:p-20">
          <div className="label-eyebrow text-secondary mb-4">Ready when you are</div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-primary leading-none">
            Source. Sell. Ship. Grow.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-xl mx-auto">Join the platform built for UK sellers who want to dispatch faster, sell smarter and grow without the gaffer tape.</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="v-btn-primary px-8 py-3 text-base" data-testid="final-cta-start">Create my account <ArrowRight size={16} /></Link>
            <Link to="/contact" className="v-btn-outline px-8 py-3 text-base" data-testid="final-cta-contact">Talk to sales</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Row({ left, right }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-2">
      <span className="text-white/80">{left}</span>
      <span className="text-white">{right}</span>
    </div>
  );
}
